import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Bell, ListChecks } from "lucide-react";
import type { NotificationList, NotificationSeverity } from "@cx/contracts";
import { HeaderDropdown, StatusBadge, type Tone } from "@cx/ui";
import { notificationBadge } from "../pages/fiscal/labels";
import { useSession } from "../session";
import { dateTime } from "../format";
import { JOB_KIND, JOB_STATUS, useJobs } from "../jobs";

/** RF-UI-05: últimas ações assíncronas no header, com contador do que está em andamento (FE25). */
export function AsyncJobsDropdown() {
  const jobs = useJobs();
  const all = jobs.data?.data ?? [];
  const running = all.filter((j) => j.status === "queued" || j.status === "running").length;
  return (
    <HeaderDropdown
      label={running ? `Ações assíncronas, ${running} em andamento` : "Ações assíncronas"}
      icon={<ListChecks size={20} aria-hidden="true" />}
      badge={running}
    >
      <div className="px-3 py-2 fw-semibold border-bottom">Ações assíncronas</div>
      {jobs.isError ? (
        <p className="px-3 py-2 mb-0 text-danger">Não foi possível carregar as ações.</p>
      ) : jobs.isLoading ? (
        <p className="px-3 py-2 mb-0 text-muted">Buscando ações…</p>
      ) : all.length === 0 ? (
        <p className="px-3 py-2 mb-0 text-muted">Nenhuma ação ainda.</p>
      ) : (
        <ul className="list-unstyled mb-0">
          {all.slice(0, 5).map((j) => (
            <li
              key={j.id}
              className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between gap-2"
            >
              <span>
                <span className="d-block">{JOB_KIND[j.kind] ?? j.kind}</span>
                <small className="text-muted">{dateTime(j.createdAt)}</small>
              </span>
              <StatusBadge tone={JOB_STATUS[j.status][0]}>{JOB_STATUS[j.status][1]}</StatusBadge>
            </li>
          ))}
        </ul>
      )}
      <div className="px-3 py-2">
        <Link to="/preferencias/acoes">Ver todas as ações</Link>
      </div>
    </HeaderDropdown>
  );
}

const SEVERITY: Record<NotificationSeverity, [Tone, string]> = {
  info: ["info", "Aviso"],
  warning: ["warning", "Atenção"],
  danger: ["danger", "Urgente"],
};

/** Sino (T2.1): avisos do sistema da empresa (ex.: vencimento do certificado A1), lidos por usuário. */
export function NotificationsDropdown() {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const key = ["notifications", companyId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<NotificationList>("/notifications"),
    refetchInterval: 5 * 60_000,
  });
  const done = () => void qc.invalidateQueries({ queryKey: key });
  const read = useMutation({
    mutationFn: (id: string) => api<void>(`/notifications/${id}/read`, { method: "POST" }),
    onSuccess: done,
  });
  const readAll = useMutation({
    mutationFn: () => api<{ updated: number }>("/notifications/read-all", { method: "POST" }),
    onSuccess: done,
  });
  const all = q.data?.data ?? [];
  const unread = notificationBadge(q.data?.unread ?? 0);
  return (
    <HeaderDropdown
      label={unread ? `Notificações, ${unread} não lidas` : "Notificações"}
      icon={<Bell size={20} aria-hidden="true" />}
      badge={unread}
    >
      <div className="px-3 py-2 fw-semibold border-bottom d-flex align-items-center justify-content-between gap-2">
        <span>Notificações</span>
        {unread > 0 && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            disabled={readAll.isPending}
            onClick={() => readAll.mutate()}
          >
            Marcar todas como lidas
          </button>
        )}
      </div>
      {q.isError ? (
        <p className="px-3 py-2 mb-0 text-danger">Não foi possível carregar as notificações.</p>
      ) : q.isLoading ? (
        <p className="px-3 py-2 mb-0 text-muted">Buscando notificações…</p>
      ) : all.length === 0 ? (
        <p className="px-3 py-2 mb-0 text-muted">Nenhuma notificação.</p>
      ) : (
        <ul className="list-unstyled mb-0">
          {all.slice(0, 10).map((n) => (
            <li key={n.id} className="px-3 py-2 border-bottom">
              <div className="d-flex align-items-center justify-content-between gap-2">
                <span className={n.read ? "" : "fw-semibold"}>
                  {n.link ? (
                    <Link to={n.link as "/"} onClick={() => !n.read && read.mutate(n.id)}>
                      {n.title}
                    </Link>
                  ) : (
                    n.title
                  )}
                  {!n.read && <span className="visually-hidden"> (não lida)</span>}
                </span>
                <StatusBadge tone={SEVERITY[n.severity][0]}>{SEVERITY[n.severity][1]}</StatusBadge>
              </div>
              <small className="d-block text-muted">{n.body}</small>
              <small className="text-muted">{dateTime(n.createdAt)}</small>
            </li>
          ))}
        </ul>
      )}
    </HeaderDropdown>
  );
}
